<template>
    <vx-data-table
        :columns="dataTableColumns"
        :rows="groups"
        rowKey="group_id"
        :loading="tableLoading && !keepingRows"
        :totalCount="grouped ? null : totalCount"
        :hasMore="hasMore"
        :loadingMore="loadingMore"
        :sections="sections"
        toolbar
        :groupByOptions="kioskTeamGroupGroupByOptions"
        searchPlaceholder="Group name, external ID, phone number or owner email"
        :columnsDescription="columnsDescription"
        :filterFields="filterFields"
        :search="search"
        :filters="filters"
        v-model:sort="sort"
        v-model:density="density"
        :visibleColumns="visibleColumnsWithinLimit"
        v-model:groupBy="groupBy"
        v-model:collapsedSections="collapsedSections"
        @update:search="updateSearch"
        @update:filters="updateFilters"
        @update:visibleColumns="updateVisibleColumns"
        @loadMore="loadMore"
    >
        <template v-slot:toolbar-actions>
            <vx-dropdown placement="right">
                <vx-button data-test="more-actions" type="button" color="white" shadow aria-label="More actions">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>
                <template v-slot:items>
                    <vx-dropdown-item data-test="export-groups" @click="exportModalVisible = true">
                        Export
                        <font-awesome-icon widthAuto :icon="faFileExport" />
                    </vx-dropdown-item>
                    <router-dropdown-item data-test="import-groups" :to="groupImportHref(props.teamId)">
                        Import
                        <font-awesome-icon widthAuto :icon="faFileArrowUp" />
                    </router-dropdown-item>
                </template>
            </vx-dropdown>
            <vx-button data-test="new-group" color="primary" :href="newGroupHref(props.teamId)">
                New Group
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </template>

        <template v-slot:views>
            <kiosk-team-group-views
                :teamId="props.teamId"
                :activeView="activeView"
                :viewState="viewState"
                :unsaved="unsaved"
                @select="selectView"
                @saved="activeView = $event"
                @updated="replaceActiveView"
                @unavailable="leaveActiveView"
                @deleted="forgetActiveView"
            />
        </template>

        <template v-slot:cell-name="{ row }">
            <span class="flex min-w-0 items-center gap-2">
                <vx-popover
                    v-if="!externalIdVisible"
                    hover
                    teleport
                    :arrow="true"
                    placement="top"
                    class="shrink-0 cursor-pointer"
                    @click="copyText(row.group_id)"
                >
                    <vx-badge data-test="group-id-badge" color="slate" size="xs">
                        <copy-button label="external ID" :value="row.group_id">
                            {{ shortGroupId(row.group_id) }}
                        </copy-button>
                    </vx-badge>
                    <template v-slot:content>
                        <div class="text-center">
                            {{ row.group_id }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <a
                    data-test="group-link"
                    :href="groupHref(props.teamId, row)"
                    class="flex min-w-0 font-bold text-slate-800"
                >
                    <vx-badge v-if="row.setup_required" data-test="setup-required" color="rose" size="xs">
                        Setup Required <font-awesome-icon widthAuto :icon="faAsterisk" class="text-rose-500" />
                    </vx-badge>
                    <vx-data-table-truncated-text v-else :text="row.name" />
                </a>
            </span>
        </template>

        <template v-slot:cell-status="{ row }">
            <group-status-badge :status="row.status" />
        </template>

        <template v-slot:cell-owner="{ row }">
            <kiosk-team-group-owner-cell
                :owner="row.owner"
                :ownerEmail="row.owner_email"
                :ownerInvite="row.owner_invite"
            />
        </template>

        <template v-slot:cell-signup="{ row }">
            <kiosk-team-group-signup-cell
                :teamId="props.teamId"
                :groupId="row.group_id"
                :signup="row.signup"
                :owner="row.owner"
                :ownerEmail="row.owner_email"
                :ownerInviteId="row.owner_invite_id"
                :ownerLastLoginAt="row.owner_last_login_at"
                :ownerPredatesLoginRecords="row.owner_predates_login_records"
                @resent="refreshAfterInviteResent"
            />
        </template>

        <template v-slot:cell-phone_number="{ row }">
            <kiosk-team-group-number-cell
                :phoneNumber="row.phone_number"
                :phoneNumbersCount="row.phone_numbers_count"
                :numberState="row.number_state"
            />
        </template>

        <template v-slot:cell-terms="{ row }">
            <kiosk-team-group-terms-badge :terms="row.terms" :termsDue="row.terms_due" />
        </template>

        <template v-slot:cell-billing="{ row }">
            <kiosk-team-group-billing-badge
                :billing="row.billing"
                :hasRates="row.has_rates"
                :hasBillingAddress="row.has_billing_address"
                :paymentMethod="row.payment_method"
                :paymentTermsDays="row.payment_terms_days"
                :orgUnitName="row.org_unit_name"
                :orgUnitHref="row.org_unit_id === null ? '' : orgUnitHref(props.teamId, row.org_unit_id)"
            />
        </template>

        <template v-slot:cell-ten_dlc_campaign_status="{ row }">
            <kiosk-team-group-ten-dlc-campaign-cell
                :status="row.ten_dlc_campaign_status"
                :campaignId="row.ten_dlc_campaign_id"
                :label="row.ten_dlc_campaign_label"
                :brandVerified="row.ten_dlc_campaign_brand_verified"
                :vettingScore="row.ten_dlc_campaign_brand_vetting_score"
                :useCase="row.ten_dlc_campaign_use_case"
                :attClass="row.ten_dlc_campaign_att_class"
                :attachOrderStatus="row.ten_dlc_attach_order_status"
                :attachOrderError="row.ten_dlc_attach_order_error"
                :attachOrderErrorCode="row.ten_dlc_attach_order_error_code"
                :attachOrderPlacedAt="row.ten_dlc_attach_order_placed_at"
            />
        </template>

        <template v-slot:cell="{ row, column }">
            <vx-data-table-truncated-text
                v-if="plainCellText(row, column.key) !== ''"
                :text="plainCellText(row, column.key)"
            />
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:cell-users_count="{ row }">
            {{ formatNumber(row.users_count) }}
        </template>

        <template v-slot:cell-org_unit_id="{ row }">
            <vx-data-table-truncated-text v-if="row.org_unit_id !== null" :text="row.org_unit_name">
                <a data-test="org-unit-link" :href="orgUnitHref(props.teamId, row.org_unit_id)">
                    {{ row.org_unit_name }}
                </a>
            </vx-data-table-truncated-text>
        </template>

        <template v-slot:header-collections_count="{ column }">
            <vx-popover hover teleport :arrow="true" placement="top">
                <span>{{ column.label }}</span>
                <template v-slot:content>
                    <div class="max-w-[240px] text-center">
                        Corporate collections the group belongs to. OU collections are not counted.
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:cell-created_at="{ row }">
            <vx-popover v-if="row.created_at" hover teleport :arrow="true" placement="top">
                <span>{{ shortDate(row.created_at) }}</span>
                <template v-slot:content>{{ shortDateTime(row.created_at) }}</template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:section-header-status="{ section }">
            <group-status-badge v-if="isGroupStatus(section.value)" :status="section.value" />
        </template>

        <template v-slot:section-count="{ section }">
            {{ pluralized('group', section.total, true) }}
        </template>

        <template v-slot:section-header-org_unit_id="{ section }">
            <span v-if="sectionNames[section.key]">{{ sectionNames[section.key] }}</span>
            <span v-else class="text-slate-500">No OU</span>
        </template>

        <template v-slot:section-header-terms="{ section }">
            <kiosk-team-group-terms-badge v-if="isTerms(section.value)" :terms="section.value" />
        </template>

        <template v-slot:actions="{ row }">
            <vx-button
                v-if="row.setup_required"
                data-test="setup-group"
                :href="groupHref(props.teamId, row)"
                color="danger"
                size="xs"
            >
                Setup
                <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
            <kiosk-team-group-row-actions
                v-else
                :teamId="props.teamId"
                :group="row"
                :groupHref="groupHref(props.teamId, row)"
                @statusUpdated="updateGroupStatus(row.group_id, $event)"
                @deleted="removeGroup(row.group_id)"
            />
        </template>

        <template v-slot:empty>
            <div v-if="narrowed" class="flex flex-col items-center gap-6 text-xl text-slate-500">
                Nothing matches these filters
                <vx-button data-test="clear-filters" color="muted-dark" @click="clearFilters"
                    >Clear filters

                    <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </div>
            <div v-else class="flex flex-col items-center gap-6 text-xl text-slate-500">
                No Groups yet
                <vx-button data-test="empty-new-group" :href="newGroupHref(props.teamId)">
                    New Group
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </div>
        </template>

        <template v-if="loadProgress !== null" v-slot:footer>
            {{ loadProgress }}
        </template>
    </vx-data-table>

    <kiosk-team-groups-export-modal
        v-model:visible="exportModalVisible"
        :teamId="props.teamId"
        :query="query"
        :sort="sort"
        :filters="filters"
        :tableColumns="columns"
        :visibleColumns="visibleColumnsWithinLimit"
        :matchingCount="grouped ? null : totalCount"
        :sortedGroups="sortedGroups"
    />
</template>

<script lang="ts" setup>
import {
    faAsterisk,
    faCirclePlus,
    faCircleX,
    faEllipsis,
    faFileArrowUp,
    faFileExport,
    faFilePen,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDataTable,
    VxDataTableTruncatedText,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    type VxDataTableColumn,
    type VxDataTableDensity,
    type VxDataTableFilterSelectable,
    type VxDataTableLoadMore,
} from '@voxie/frontend-components';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import CopyButton from '~/components/general/CopyButton.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import RouterDropdownItem from '~/components/general/RouterDropdownItem.vue';
import { useCopyText } from '~/composables/useCopyText';
import { useQueryParams } from '~/composables/useQueryString';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { useToasted } from '~/composables/useToasted';
import { adminOrgUnitsClient, adminTeamGroupsClient } from '~/services';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import type { GroupStatus } from '~/constants/groupStatuses';
import type {
    KioskTeamGroup,
    KioskTeamGroupGroupBy,
    KioskTeamGroupSectionsParams,
} from '~/services/adminTeamGroupsClient.types';
import type { KioskTeamGroupView } from '~/services/adminTeamGroupViewsClient.types';
import { shortDate, shortDateTime } from '~/utils/date';
import debounce from '~/utils/debounce';
import { formatNumber, isEqual } from '~/utils/helpers';
import { catchRequestError } from '~/utils/request';
import KioskTeamGroupRowActions from './KioskTeamGroupRowActions.vue';
import KioskTeamGroupsExportModal from './KioskTeamGroupsExportModal.vue';
import KioskTeamGroupBillingBadge from './KioskTeamGroupBillingBadge.vue';
import KioskTeamGroupNumberCell from './KioskTeamGroupNumberCell.vue';
import KioskTeamGroupOwnerCell from './KioskTeamGroupOwnerCell.vue';
import KioskTeamGroupSignupCell from './KioskTeamGroupSignupCell.vue';
import KioskTeamGroupTenDlcCampaignCell from './KioskTeamGroupTenDlcCampaignCell.vue';
import KioskTeamGroupTermsBadge from './KioskTeamGroupTermsBadge.vue';
import KioskTeamGroupViews from './KioskTeamGroupViews.vue';
import {
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_COLUMNS,
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_FILTERS,
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_VALUE_LENGTH,
    KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES,
    awaitsRows,
    columnsStorageKey,
    customAttributeColumns,
    customAttributeKeysOf,
    densityStorageKey,
    exceedsCustomAttributeColumnLimit,
    exceedsCustomAttributeFilterLimit,
    exceedsCustomAttributeValueLength,
    exceedsFilterValueLimit,
    expandedSectionKeys,
    fetchAllGroups,
    filtersAllowStatus,
    groupHref,
    groupImportHref,
    isColumnVisible,
    isGroupStatus,
    isNarrowed,
    isSameViewState,
    isTerms,
    kioskTeamGroupColumns,
    kioskTeamGroupFilterFields,
    kioskTeamGroupGroupByOptions,
    loadedGroupsLabel,
    needsReload,
    newGroupHref,
    nextCursorFrom,
    nextGroupsWindow,
    orgUnitHref,
    noOrgUnitOption,
    plainCellText,
    savedViewState,
    sectionCursorsFrom,
    sectionNamesFrom,
    sectionOfGroup,
    shortGroupId,
    sortRowsInBrowser,
    sortsInBrowser,
    tableFiltersOf,
    toBrowserSections,
    toDataTableSection,
    toGroupSectionsParams,
    toGroupsCountParams,
    toGroupsParams,
    toSearchQuery,
    toSectionPageParams,
    toViewState,
    unloadedSectionKeys,
    withGroupStatus,
    withNextSectionRows,
    withResortedSectionRows,
    withSectionGroupStatus,
    withTrimmedCustomAttributeValues,
    withinCustomAttributeColumnLimit,
    withoutSectionGroup,
    withoutUnlistedCustomAttributeColumns,
    type KioskTeamGroupColumn,
    type KioskTeamGroupsFilter,
    type KioskTeamGroupsSection,
    type KioskTeamGroupsSort,
} from './kioskTeamGroupsTableHelpers';

interface OrgUnitOption {
    value: string;
    label: string;
}

interface AdminOrgUnit {
    id: number;
    name: string;
}

interface OrgUnitLookupParams {
    query?: string;
    ids?: string[];
}

const props = defineProps<{
    teamId: number;
    initialView: KioskTeamGroupView | null;
}>();

const toasted = useToasted();
const { copyText } = useCopyText();
const urlQuery = useQueryParams<{ view?: number }>();

const groupsCancelKey = Symbol('kiosk-team-groups');
const countCancelKey = Symbol('kiosk-team-groups-count');
const sectionsCancelKey = Symbol('kiosk-team-group-sections');
const sectionPageCancelKeys = new Map<string, symbol>();

const groups = ref<KioskTeamGroup[]>([]);
const sortedGroups = ref<KioskTeamGroup[] | null>(null);
const loadedCount = ref<number | null>(null);
const nextCursor = ref<string | null>(null);
const totalCount = ref<number | null>(null);
const loading = ref(true);
const sectionsLoading = ref(false);
const loadingMore = ref(false);
// The rows stay through the reload a resent invite starts, so its signup keeps the focus it got back.
const keepingRows = ref(false);

const activeView = ref<KioskTeamGroupView | null>(props.initialView);
const rememberedColumns = useLocalStorage<string[] | null>(() => columnsStorageKey(props.teamId), null, {
    serializer: StorageSerializers.object,
});
const viewColumnsKept = ref(false);
const savedState = computed(() => savedViewState(activeView.value, rememberedColumns.value));
const savedColumns = (): string[] | null =>
    activeView.value === null ? rememberedColumns.value : savedState.value.visibleColumns;

const search = ref(savedState.value.search);
const query = ref(savedState.value.search);
const sort = ref<KioskTeamGroupsSort>(savedState.value.sort);
const filters = ref<KioskTeamGroupsFilter[]>(tableFiltersOf(savedState.value));
const groupBy = ref<KioskTeamGroupGroupBy | null>(savedState.value.groupBy);
const visibleColumns = ref<string[] | null>(savedColumns());

const sections = ref<KioskTeamGroupsSection[] | null>(null);
const sectionNames = ref<Record<string, string | null>>({});
const sectionCursors = ref<Record<string, string | null>>({});
const collapsedSections = ref<string[]>([]);
const exportModalVisible = ref(false);
const customAttributeKeys = ref<string[]>([]);
const customAttributeKeysTruncated = ref(false);

const density = useLocalStorage<VxDataTableDensity>(() => densityStorageKey(props.teamId), 'comfortable', {
    writeDefaults: false,
});

const {
    loading: orgUnitsLoading,
    response: orgUnitSearchResults,
    createSearch: createOrgUnitSearch,
    cancelSearch: cancelOrgUnitSearch,
    cancelKey: orgUnitsCancelKey,
} = useSearchRequest<OrgUnitOption[]>(adminOrgUnitsClient, 'kiosk-team-group-org-units');

const knownOrgUnits = ref(new Map<string, OrgUnitOption>());

const selectedOrgUnitIds = computed(() =>
    (filters.value.find((filter) => filter.key === 'org_unit_id')?.values ?? []).filter(
        (value) => value !== noOrgUnitOption.value
    )
);

const toOrgUnitOption = (orgUnit: AdminOrgUnit): OrgUnitOption => ({ value: String(orgUnit.id), label: orgUnit.name });

const fetchOrgUnits = async (params: OrgUnitLookupParams, config?: ExtendedAxiosRequestConfig) => {
    const { data } = await adminOrgUnitsClient.listOrgUnits(
        { team_id: props.teamId, sort_field: 'name', sort_dir: 'asc', ...params },
        config
    );

    return (data.data as AdminOrgUnit[]).map(toOrgUnitOption);
};

const rememberOrgUnits = (options: OrgUnitOption[]) => {
    options.forEach((option) => knownOrgUnits.value.set(option.value, option));
};

const searchOrgUnits = createOrgUnitSearch((orgUnitQuery: string) =>
    fetchOrgUnits({ query: orgUnitQuery }, { cancelKey: orgUnitsCancelKey })
);

const resolveSelectedOrgUnits = async (ids: string[]) => {
    const unknownIds = ids.filter((id) => !knownOrgUnits.value.has(id));

    if (unknownIds.length) {
        rememberOrgUnits(
            await fetchOrgUnits({ ids: unknownIds }).catch(() => {
                toasted.global.platform_error();
                return [];
            })
        );
    }
};

const orgUnitOptions = computed(() => {
    const selectedIds = new Set(selectedOrgUnitIds.value);
    const selectedOptions = selectedOrgUnitIds.value.flatMap((id) => knownOrgUnits.value.get(id) ?? []);
    const unselectedResults = (orgUnitSearchResults.value ?? []).filter((option) => !selectedIds.has(option.value));

    return [...selectedOptions, ...unselectedResults];
});

const orgUnitSelectable = computed<VxDataTableFilterSelectable>(() => ({
    options: orgUnitOptions.value,
    label: 'label',
    reduce: (option: OrgUnitOption) => option.value,
    loading: orgUnitsLoading.value,
    placeholder: 'Search organizational units',
    onSearch: (orgUnitQuery: string) => searchOrgUnits(orgUnitQuery),
}));

const visibleColumnsWithinLimit = computed(() => withinCustomAttributeColumnLimit(visibleColumns.value));
const visibleCustomAttributeKeys = computed(() => customAttributeKeysOf(visibleColumnsWithinLimit.value ?? []));
const columns = computed<KioskTeamGroupColumn[]>(() => [
    ...kioskTeamGroupColumns,
    ...customAttributeColumns([...new Set([...customAttributeKeys.value, ...visibleCustomAttributeKeys.value])]),
]);
const dataTableColumns = computed(() => columns.value as VxDataTableColumn<KioskTeamGroup>[]);
const filterFields = computed(() =>
    kioskTeamGroupFilterFields(
        orgUnitSelectable.value,
        customAttributeKeys.value,
        customAttributeKeysTruncated.value,
        filters.value
    )
);
const countParams = computed(() => toGroupsCountParams(query.value, filters.value));
const listParams = computed(() =>
    toGroupsParams(query.value, sort.value, filters.value, visibleCustomAttributeKeys.value)
);
const sectionsParams = computed(() =>
    groupBy.value === null
        ? null
        : toGroupSectionsParams(query.value, sort.value, filters.value, groupBy.value, visibleCustomAttributeKeys.value)
);
const customAttributeColumnLimitMessage = `Show up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_COLUMNS} custom attribute columns at a time.`;
const columnsDescription = computed(() => {
    const listedKeyCount = customAttributeKeys.value.length;

    if (listedKeyCount === 0) {
        return undefined;
    }

    return customAttributeKeysTruncated.value
        ? `${customAttributeColumnLimitMessage} Only the first ${listedKeyCount} custom attributes are listed.`
        : customAttributeColumnLimitMessage;
});
const grouped = computed(() => groupBy.value !== null);
const tableLoading = computed(() => (grouped.value ? sectionsLoading.value : loading.value));
const sortedInBrowser = computed(() => sortsInBrowser(sort.value));
const narrowed = computed(() => isNarrowed(countParams.value));
const hasMore = computed(() =>
    sortedGroups.value === null ? nextCursor.value !== null : groups.value.length < sortedGroups.value.length
);
const loadProgress = computed(() =>
    loadedCount.value === null ? null : loadedGroupsLabel(loadedCount.value, totalCount.value)
);
const externalIdVisible = computed(() => isColumnVisible(visibleColumns.value, 'group_id'));
const viewState = computed(() =>
    toViewState(search.value, sort.value, filters.value, groupBy.value, visibleColumns.value)
);
const unsaved = computed(() => !isSameViewState(viewState.value, savedState.value));

const loadCustomAttributeKeys = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupCustomAttributeKeys(props.teamId);

        customAttributeKeys.value = data.data;
        customAttributeKeysTruncated.value = data.truncated;
        rememberedColumns.value = withoutUnlistedCustomAttributeColumns(rememberedColumns.value, data.data);
    } catch {
        customAttributeKeys.value = [];
        toasted.global.platform_error();
    }
};

const showLoadedCount = (count: number) => {
    loadedCount.value = count;
};

const stopLoadingAllGroups = () => {
    if (loadedCount.value !== null) {
        adminTeamGroupsClient.cancelRequest(groupsCancelKey);
    }

    sortedGroups.value = null;
    loadedCount.value = null;
};

const loadAllGroups = async () => {
    loading.value = true;
    loadingMore.value = false;
    nextCursor.value = null;
    sortedGroups.value = null;
    loadedCount.value = 0;

    try {
        const sorted = sortRowsInBrowser(
            await fetchAllGroups(props.teamId, listParams.value, groupsCancelKey, showLoadedCount),
            sort.value
        );

        sortedGroups.value = sorted;
        groups.value = nextGroupsWindow(sorted, 0);
        loadedCount.value = null;
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        groups.value = [];
        totalCount.value = null;
        loadedCount.value = null;
        loading.value = false;
        toasted.global.platform_error();
    }
};

const loadGroups = async () => {
    if (sortedInBrowser.value) {
        loadAllGroups();
        return;
    }

    stopLoadingAllGroups();
    loading.value = true;
    loadingMore.value = false;
    nextCursor.value = null;

    try {
        const { data } = await adminTeamGroupsClient.getGroups(props.teamId, listParams.value, {
            cancelKey: groupsCancelKey,
        });

        groups.value = data.data;
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        groups.value = [];
        totalCount.value = null;
        loading.value = false;
        toasted.global.platform_error();
    }
};

const showMoreGroups = () => {
    if (sortedGroups.value !== null) {
        groups.value = nextGroupsWindow(sortedGroups.value, groups.value.length);
    }
};

const loadMoreGroups = async () => {
    if (sortedInBrowser.value) {
        showMoreGroups();
        return;
    }

    loadingMore.value = true;

    try {
        const { data } = await adminTeamGroupsClient.getGroups(
            props.teamId,
            { ...listParams.value, cursor: nextCursor.value },
            { cancelKey: groupsCancelKey }
        );

        groups.value = [...groups.value, ...data.data];
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loadingMore.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loadingMore.value = false;
        toasted.global.platform_error();
    }
};

const sectionPageCancelKey = (sectionKey: string): symbol => {
    const cancelKey = sectionPageCancelKeys.get(sectionKey) ?? Symbol(`kiosk-team-group-section-${sectionKey}`);
    sectionPageCancelKeys.set(sectionKey, cancelKey);

    return cancelKey;
};

const cancelSectionPageRequests = () => {
    sectionPageCancelKeys.forEach((cancelKey) => adminTeamGroupsClient.cancelRequest(cancelKey));
    sectionPageCancelKeys.clear();
};

const resetSections = () => {
    cancelSectionPageRequests();
    sections.value = null;
    sectionCursors.value = {};
    sectionNames.value = {};
    collapsedSections.value = [];
};

const loadAllSections = async (params: KioskTeamGroupSectionsParams) => {
    sectionsLoading.value = true;
    sections.value = sections.value?.map((section) => ({ ...section, hasMore: false })) ?? null;
    sortedGroups.value = null;
    loadedCount.value = 0;
    loadCount();

    try {
        const [{ data }, allGroups] = await Promise.all([
            adminTeamGroupsClient.getGroupSections(props.teamId, params, { cancelKey: sectionsCancelKey }),
            fetchAllGroups(props.teamId, listParams.value, groupsCancelKey, showLoadedCount),
        ]);
        const sorted = sortRowsInBrowser(allGroups, sort.value);

        sortedGroups.value = sorted;
        sectionNames.value = sectionNamesFrom(data.data);
        sections.value = toBrowserSections(params.group_by, data.data, sorted);
        collapsedSections.value = unloadedSectionKeys(data.data);
        loadedCount.value = null;
        sectionsLoading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        stopLoadingAllGroups();
        adminTeamGroupsClient.cancelRequest(sectionsCancelKey);
        sections.value = [];
        sectionsLoading.value = false;
        toasted.global.platform_error();
    }
};

const loadSections = async (params: KioskTeamGroupSectionsParams, keepCurrentSections = false) => {
    if (keepCurrentSections) {
        cancelSectionPageRequests();
    } else {
        resetSections();
    }

    if (sortedInBrowser.value) {
        loadAllSections(params);
        return;
    }

    stopLoadingAllGroups();
    sectionsLoading.value = true;
    sections.value = sections.value?.map((section) => ({ ...section, loading: true })) ?? null;

    try {
        const { data } = await adminTeamGroupsClient.getGroupSections(props.teamId, params, {
            cancelKey: sectionsCancelKey,
        });

        sectionCursors.value = sectionCursorsFrom(data.data);
        sectionNames.value = sectionNamesFrom(data.data);
        sections.value = data.data.map(toDataTableSection(params.group_by));
        collapsedSections.value = unloadedSectionKeys(data.data);
        sectionsLoading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        sections.value = [];
        sectionsLoading.value = false;
        toasted.global.platform_error();
    }
};

const updateSection = (sectionKey: string, update: (section: KioskTeamGroupsSection) => KioskTeamGroupsSection) => {
    if (sections.value === null) {
        return;
    }

    sections.value = sections.value.map((section) => (section.key === sectionKey ? update(section) : section));
};

const showMoreSectionRows = (sectionKey: string) => {
    const sorted = sortedGroups.value;
    const sectionGroupBy = groupBy.value;

    if (sorted === null || sectionGroupBy === null) {
        return;
    }

    updateSection(sectionKey, (section) => withNextSectionRows(section, sectionGroupBy, sorted));
};

const loadSectionPage = async (sectionKey: string) => {
    if (sortedInBrowser.value) {
        showMoreSectionRows(sectionKey);
        return;
    }

    const params = sectionsParams.value;

    if (params === null) {
        return;
    }

    updateSection(sectionKey, (section) => ({ ...section, loading: true }));

    try {
        const { data } = await adminTeamGroupsClient.getGroups(
            props.teamId,
            toSectionPageParams(params, sectionKey, sectionCursors.value[sectionKey] ?? null),
            { cancelKey: sectionPageCancelKey(sectionKey) }
        );
        const cursor = nextCursorFrom(data.next_page_url);

        sectionCursors.value = { ...sectionCursors.value, [sectionKey]: cursor };
        updateSection(sectionKey, (section) => ({
            ...section,
            rows: [...section.rows, ...data.data],
            hasMore: cursor !== null,
            loading: false,
        }));
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        updateSection(sectionKey, (section) => ({ ...section, loading: false }));
        toasted.global.platform_error();
    }
};

const loadExpandedSections = (collapsed: string[], previouslyCollapsed: string[]) => {
    const expandedKeys = expandedSectionKeys(collapsed, previouslyCollapsed);

    (sections.value ?? [])
        .filter((section) => expandedKeys.includes(section.key) && awaitsRows(section))
        .forEach((section) => loadSectionPage(section.key));
};

const loadMore = ({ key }: VxDataTableLoadMore) => {
    if (key === null) {
        loadMoreGroups();
        return;
    }

    loadSectionPage(key);
};

const loadCount = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupsCount(props.teamId, countParams.value, {
            cancelKey: countCancelKey,
        });

        totalCount.value = data.count;
    } catch (error) {
        if (!isCancel(error)) {
            totalCount.value = null;
            toasted.error(catchRequestError(error));
        }
    }
};

const reloadGroups = () => {
    if (sectionsParams.value === null) {
        loadGroups();
        return;
    }

    loadSections(sectionsParams.value, true);
};

const reloadCountUnlessGrouped = () => {
    if (!grouped.value) {
        loadCount();
    }
};

const showGroupsList = () => {
    adminTeamGroupsClient.cancelRequest(sectionsCancelKey);
    resetSections();
    loadGroups();
    loadCount();
};

const changeGrouping = () => {
    if (sectionsParams.value === null) {
        showGroupsList();
        return;
    }

    loadSections(sectionsParams.value);
};

const applySearch = debounce((value: string) => {
    query.value = toSearchQuery(value);
});

const updateSearch = (value: string) => {
    search.value = value;
    applySearch(value);
};

const updateFilters = (filtersToApply: KioskTeamGroupsFilter[]) => {
    const next = withTrimmedCustomAttributeValues(filtersToApply);

    if (exceedsFilterValueLimit(next)) {
        toasted.error(`Select up to ${KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES} values per filter.`);
        return;
    }

    if (exceedsCustomAttributeFilterLimit(next)) {
        toasted.error(`Add up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_FILTERS} custom attribute filters.`);
        return;
    }

    if (exceedsCustomAttributeValueLength(next)) {
        toasted.error(
            `Type up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_VALUE_LENGTH} characters per custom attribute value.`
        );
        return;
    }

    filters.value = next;
};

const updateVisibleColumns = (next: string[] | null) => {
    if (exceedsCustomAttributeColumnLimit(next)) {
        toasted.error(customAttributeColumnLimitMessage);
        return;
    }

    if (activeView.value === null && !viewColumnsKept.value) {
        rememberedColumns.value = next;
    }

    visibleColumns.value = next;
};

const resetSearch = (value: string) => {
    applySearch.cancel();
    search.value = value;
    query.value = value;
};

const clearFilters = () => {
    resetSearch('');
    filters.value = [];
};

const selectView = (view: KioskTeamGroupView | null) => {
    activeView.value = view;
    viewColumnsKept.value = false;
    resetSearch(savedState.value.search);
    sort.value = savedState.value.sort;
    filters.value = tableFiltersOf(savedState.value);
    groupBy.value = savedState.value.groupBy;
    visibleColumns.value = savedColumns();
};

const replaceActiveView = (view: KioskTeamGroupView) => {
    if (activeView.value?.id === view.id) {
        activeView.value = view;
    }
};

const leaveActiveView = (viewId: number) => {
    if (activeView.value?.id === viewId) {
        activeView.value = null;
        viewColumnsKept.value = true;
    }
};

const forgetActiveView = (viewId: number) => {
    if (activeView.value?.id === viewId) {
        activeView.value = null;
        viewColumnsKept.value = false;
        visibleColumns.value = rememberedColumns.value;
    }
};

const updateSortedGroups = (update: (sorted: KioskTeamGroup[]) => KioskTeamGroup[]) => {
    if (sortedGroups.value !== null) {
        sortedGroups.value = update(sortedGroups.value);
        return;
    }

    // Pages read before the change would bring the group back as it was.
    if (tableLoading.value) {
        reloadGroups();
    }
};

const resortAllGroups = () => {
    if (sortedGroups.value === null) {
        // Holding no groups with nothing loading means the load failed.
        if (sortedInBrowser.value && loadedCount.value === null) {
            reloadGroups();
        }

        return;
    }

    const sorted = sortRowsInBrowser(sortedGroups.value, sort.value);

    sortedGroups.value = sorted;

    if (groupBy.value === null) {
        groups.value = sorted.slice(0, groups.value.length);
    } else if (sections.value !== null) {
        sections.value = withResortedSectionRows(sections.value, groupBy.value, sorted);
    }
};

const removeListGroup = (groupId: string) => {
    groups.value = groups.value.filter((group) => group.group_id !== groupId);

    if (groups.value.length || !hasMore.value) {
        return;
    }

    if (sortedGroups.value === null) {
        loadGroups();
    } else {
        showMoreGroups();
    }
};

const removeSectionGroup = (groupId: string) => {
    if (sections.value === null) {
        return;
    }

    const section = sectionOfGroup(sections.value, groupId);

    if (section === undefined) {
        return;
    }

    sections.value = withoutSectionGroup(sections.value, groupId);

    const emptiedSection = sections.value.find((candidate) => candidate.key === section.key && awaitsRows(candidate));

    if (emptiedSection !== undefined) {
        loadSectionPage(emptiedSection.key);
    }
};

const removeGroup = (groupId: string) => {
    updateSortedGroups((sorted) => sorted.filter((group) => group.group_id !== groupId));

    if (grouped.value) {
        removeSectionGroup(groupId);
    } else {
        removeListGroup(groupId);
    }

    reloadCountUnlessGrouped();
};

const refreshAfterInviteResent = () => {
    keepingRows.value = true;
    reloadGroups();
    reloadCountUnlessGrouped();
};

const updateGroupStatus = (groupId: string, status: GroupStatus) => {
    if (!filtersAllowStatus(filters.value, status)) {
        removeGroup(groupId);
        return;
    }

    reloadCountUnlessGrouped();

    if (groupBy.value === 'status') {
        reloadGroups();
        return;
    }

    updateSortedGroups((sorted) => withGroupStatus(sorted, groupId, status));

    if (!grouped.value) {
        groups.value = withGroupStatus(groups.value, groupId, status);
        return;
    }

    if (sections.value !== null) {
        sections.value = withSectionGroupStatus(sections.value, groupId, status);
    }
};

watch(
    [groupBy, listParams, countParams],
    ([nextGroupBy, nextList, nextCount], [previousGroupBy, previousList, previousCount]) => {
        keepingRows.value = false;

        if (nextGroupBy !== previousGroupBy) {
            changeGrouping();
            return;
        }

        if (needsReload(nextList, previousList)) {
            reloadGroups();
        } else {
            resortAllGroups();
        }

        if (!isEqual(nextCount, previousCount)) {
            reloadCountUnlessGrouped();
        }
    }
);
watch(collapsedSections, loadExpandedSections);
watch(tableLoading, (busy) => {
    if (!busy) {
        keepingRows.value = false;
    }
});
watch(rememberedColumns, (remembered) => {
    if (activeView.value === null && !viewColumnsKept.value) {
        visibleColumns.value = remembered;
    }
});
watch(
    activeView,
    (view) => {
        if (urlQuery.view !== view?.id) {
            urlQuery.view = view?.id;
        }
    },
    { immediate: true }
);

watch(orgUnitSearchResults, (next) => rememberOrgUnits(next ?? []));
watch(selectedOrgUnitIds, resolveSelectedOrgUnits, { immediate: true });

onMounted(() => {
    reloadGroups();
    reloadCountUnlessGrouped();
    loadCustomAttributeKeys();
    searchOrgUnits('');
});

onBeforeUnmount(() => {
    applySearch.cancel();
    cancelOrgUnitSearch();
    stopLoadingAllGroups();
});
</script>
